/**
 * Filtros del listado ↔ query string.
 *
 * Antes los filtros vivían solo en un store en memoria: el vendedor filtraba,
 * copiaba la dirección, y el link viajaba "pelado". Al cliente le llegaba el
 * listado completo. Tampoco sobrevivían a un F5.
 *
 * Ahora cada filtro se escribe en la URL, así que el link se comparte tal cual,
 * la página se puede recargar, y Google puede indexar las vistas filtradas.
 */

import { ORDEN_INICIAL } from '@/lib/orden-vehiculos';

/** Clave del store → nombre del parámetro. En español: el link lo lee un humano. */
export const FILTER_PARAMS: Record<string, string> = {
  brand: 'marca',
  model: 'modelo',
  category: 'categoria',
  fuel_type: 'combustible',
  condition: 'condicion',
  color: 'color',
  dealership: 'sucursal',
  year: 'anio',
  facturable: 'facturable',
  // Slugs canónicos, no el texto crudo de la base: si no, ?transmision=MECÁNICA
  // y ?transmision=manual serían links distintos para el mismo filtro. La
  // traducción la hace `@/lib/vehicleSpecs`.
  transmission: 'transmision',
  traction: 'traccion',
};

const PARAM_TO_FILTER: Record<string, string> = Object.fromEntries(
  Object.entries(FILTER_PARAMS).map(([k, v]) => [v, k])
);

/**
 * Parámetros que no son filtros pero deben sobrevivir.
 *
 * `promo`/`titulo` son campañas. `section` es funcional: /embed lo usa para
 * decidir QUÉ sección monta, y el listado del home vive también ahí dentro — sin
 * preservarlo, el primer click en un filtro dejaría al iframe sin su sección.
 */
const PRESERVED = ['promo', 'titulo', 'section'] as const;

/**
 * De los preservados, los que además sobreviven a "Limpiar filtros".
 *
 * `promo`/`titulo` NO están: limpiar en /vehicles siempre se llevó también la
 * campaña, y ese es su comportamiento documentado. `section` sí, porque no es
 * una preferencia del usuario sino la sección que /embed tiene que montar —
 * borrarla dejaría al iframe vacío en el sitio de un tercero.
 */
const FUNCIONALES = ['section'] as const;

const PRICE = 'precio';
const QUERY = 'q';
const SORT = 'orden';

/**
 * Query string que debe quedar tras limpiar los filtros: lo funcional y el
 * orden. Vive acá y no en el hook para que la lista de qué es filtro y qué no
 * exista en un solo lugar.
 *
 * `orden` sobrevive porque ordenar no es filtrar: no saca ningún auto de la
 * lista, sólo cambia en qué orden se ve. "Limpiar filtros" devuelve la lista
 * completa, y la devuelve en el orden que el usuario ya había elegido — igual
 * que hace el store (`clearFilters` conserva `sortOrder`).
 */
export function clearedSearchParams(current: URLSearchParams): URLSearchParams {
  const sp = new URLSearchParams();
  for (const key of FUNCIONALES) {
    const v = current.get(key);
    if (v) sp.set(key, v);
  }
  const orden = current.get(SORT);
  if (orden) sp.set(SORT, orden);
  return sp;
}

/**
 * `true` si la URL trae algo que ACOTA el listado: filtros, precio, búsqueda o
 * pestaña. El orden no cuenta. Es la señal que espera `useVehicleFiltersUrl`
 * para saber que "Limpiar" ya terminó de vaciar la URL: si mirara `hasAny`,
 * un `orden=` conservado lo dejaría esperando para siempre.
 */
export function hayFiltrosEnUrl(sp: URLSearchParams): boolean {
  const p = searchParamsToFilters(sp);
  return (
    Object.keys(p.filters).length > 0 ||
    p.priceRange !== null ||
    p.searchQuery !== null ||
    p.categoryTab !== null
  );
}
/**
 * Pestaña de categoría del listado del home (NewVehiclesSection).
 *
 * NO es `categoria`: ese parámetro lleva ids de la tabla de categorías y lo usa
 * /vehicles. Esta pestaña filtra por NOMBRE con variantes ('SUV' matchea
 * "Crossover", "Todoterreno", "4x4"…), así que son dos ejes distintos y
 * mezclarlos haría que un link del home abriera /vehicles vacío.
 */
const CATEGORY_TAB = 'tipo';
/**
 * La pestaña de ofertas tiene un id interno ('__offers__') que no da para un
 * link que lee una persona. En la URL viaja con este nombre; quien llama al
 * hook traduce en los dos sentidos.
 */
export const OFFER_TAB_PARAM = 'ofertas';

const asArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.map(String) : v === undefined || v === null || v === '' ? [] : [String(v)];

export interface FiltersUrlState {
  filters: Record<string, unknown>;
  priceRange: [number, number];
  sortOrder: string;
  searchQuery: string;
  /**
   * Pestaña de categoría, sólo en las superficies que la tienen (el listado del
   * home). Sin este campo el query string sale idéntico al de antes, que es lo
   * que mantiene a /vehicles sin cambios.
   */
  categoryTab?: string;
}

/**
 * Construye el query string. Omite todo lo que esté en su valor por defecto,
 * para que un listado sin filtrar tenga una URL limpia.
 */
export function filtersToSearchParams(
  state: FiltersUrlState,
  maxPrice: number,
  current: URLSearchParams
): URLSearchParams {
  const sp = new URLSearchParams();

  for (const key of PRESERVED) {
    const v = current.get(key);
    if (v) sp.set(key, v);
  }

  for (const [filterKey, param] of Object.entries(FILTER_PARAMS)) {
    const values = asArray(state.filters[filterKey]);
    if (values.length) sp.set(param, values.join(','));
  }

  const [min, max] = state.priceRange;
  const priceTouched = min > 0 || (maxPrice > 0 && max > 0 && max < maxPrice);
  if (priceTouched) sp.set(PRICE, `${min}-${max}`);

  if (state.searchQuery.trim()) sp.set(QUERY, state.searchQuery.trim());
  // El orden que NO ensucia el link es el PREDETERMINADO. Hasta el ticket #164
  // ése era 'date_desc'; ahora es 'default' (el único donde manda la prioridad
  // manual). Dejar la comparación vieja tenía dos efectos, los dos malos:
  // `orden=default` se escribía en todos los links, y elegir "Recientes
  // primero" NO se escribía — así que al recargar se perdía la elección del
  // visitante y volvía a mandar la prioridad, justo lo contrario de la regla.
  if (state.sortOrder && state.sortOrder !== ORDEN_INICIAL) sp.set(SORT, state.sortOrder);
  // 'all' es "sin pestaña elegida": no ensucia el link.
  if (state.categoryTab && state.categoryTab !== 'all') {
    sp.set(CATEGORY_TAB, state.categoryTab);
  }

  return sp;
}

export interface ParsedFilters {
  filters: Record<string, string[]>;
  priceRange: [number, number] | null;
  sortOrder: string | null;
  searchQuery: string | null;
  /** Pestaña de categoría; `null` si la URL no la trae. */
  categoryTab: string | null;
  /** `true` si la URL traía al menos un filtro: evita pisar el estado si no. */
  hasAny: boolean;
}

export function searchParamsToFilters(sp: URLSearchParams): ParsedFilters {
  const filters: Record<string, string[]> = {};
  let hasAny = false;

  sp.forEach((raw, param) => {
    const key = PARAM_TO_FILTER[param];
    if (!key) return;
    const values = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (values.length) {
      filters[key] = values;
      hasAny = true;
    }
  });

  let priceRange: [number, number] | null = null;
  const price = sp.get(PRICE);
  if (price) {
    const [a, b] = price.split('-').map((n) => Number(n));
    // Un rango inválido (texto, invertido) se ignora en vez de romper el listado.
    if (Number.isFinite(a) && Number.isFinite(b) && a >= 0 && b >= a) {
      priceRange = [a, b];
      hasAny = true;
    }
  }

  const searchQuery = sp.get(QUERY);
  if (searchQuery) hasAny = true;
  const sortOrder = sp.get(SORT);
  if (sortOrder) hasAny = true;
  const categoryTab = sp.get(CATEGORY_TAB);
  if (categoryTab) hasAny = true;

  return { filters, priceRange, sortOrder, searchQuery, categoryTab, hasAny };
}
